import React, { memo, useState, Fragment, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { gql, useQuery, useMutation } from '@apollo/client'
import { LANDING_PAGE_FIELDS } from '../../graphql/landing-page-fragment'
import Card from '../../components/common/Card'
import LandingPageForm from '../../components/campaigns/LandingPageForm'
import _ from 'lodash'
import { ToastContainer } from 'react-toastify'
import { showNotification } from '../../utils/notifications'
import { CubeLoader } from '@/components/ui/cube-loader'

const GET_LANDING_PAGE = gql`
    ${LANDING_PAGE_FIELDS}
    query LandingPages($where: LandingPageWhere) {
        landingPages(where: $where) {
            ...LandingPageFields
            campaign {
                id
            }
        }
    }
`

const UPDATE_LANDING_PAGE = gql`
    ${LANDING_PAGE_FIELDS}
    mutation UpdateLandingPage($landingPageId: ID!, $landingPageInput: LandingPageInput) {
        updateLandingPage(landingPageId: $landingPageId, landingPageInput: $landingPageInput) {
            ...LandingPageFields
        }
    }
`

const GET_THEMES = gql`
    query Themes($options: ThemeOptions) {
        themes(options: $options) {
            id
            name
        }
    }
`

const EditLandingPage = memo(() => {
    const params = useParams()
    const landingPageId = params.id
    const [updatedData, setUpdatedData] = useState({})
    const [errors, setError] = useState([])
    const [originalStatus, setOriginalStatus] = useState(null)
    const [isDraft, setIsDraft] = useState(false)
    const [autosaveStatus, setAutosaveStatus] = useState(null)
    const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(false)
    const autosaveTimeoutRef = useRef(null)
    const navigate = useNavigate()

    const { data, loading } = useQuery(GET_LANDING_PAGE, {
        variables: { where: { id: landingPageId } },
        onCompleted: (data) => {
            if (data?.landingPages?.length > 0) {
                const landingPage = data.landingPages[0]
                setOriginalStatus(landingPage.status)
                setIsDraft(landingPage.status === 'DRAFT')
                console.log('Page status: ', landingPage.status)

                // Enable autosave since we're editing an existing page
                setIsAutosaveEnabled(true)
            }
        },
    })

    const themesQuery = useQuery(GET_THEMES, {
        variables: {
            options: { sort: [{ order: 'ASC' }] },
        },
    })

    const updateLandingPageCallback = async () => {
        // If we're dealing with a draft, make sure to set the status to ACTIVE
        if (isDraft) {
            try {
                await finalUpdateLandingPage({
                    variables: {
                        landingPageId: landingPageId,
                        landingPageInput: getLandingPageInput(updatedData, 'ACTIVE'),
                    },
                })
            } catch (error) {
                console.error('Error publishing landing page:', error)
                setError([{ message: 'Error publishing landing page' }])
            }
        } else {
            // Regular update for non-draft pages
            await finalUpdateLandingPage()
        }
    }

    const updateData = (data) => {
        setUpdatedData(data)
    }

    const setFormFieldsFromFormData = (formData) => {
        const formFields = []

        _.each(_.range(1, 9), (number) => {
            const label = formData[`field${number}Label`]
            if (label && label.length > 0) {
                formFields.push({
                    label,
                    required: formData[`field${number}Required`],
                    type: formData[`field${number}Type`],
                    enabled: formData[`field${number}Enabled`],
                    values: formData[`field${number}Values`],
                })
            }
        })

        return JSON.stringify(formFields)
    }

    const setButtonSettingsFromFormData = (formData) => {
        const { buttonLabel, buttonColour } = formData || {}
        return JSON.stringify({ buttonLabel, buttonColour })
    }

    const setPageSettingsFromFormData = (formData) => {
        const {
            pageHeading,
            pageHeadingColor,
            pageHeadingFont,
            pageSubHeading,
            pageSubHeadingColor,
            pageSubHeadingFont,
        } = formData || {}
        return JSON.stringify({
            pageHeading,
            pageHeadingColor,
            pageHeadingFont,
            pageSubHeading,
            pageSubHeadingColor,
            pageSubHeadingFont,
        })
    }

    const setPagePopupSettingsFromFormData = (formData) => {
        const { popupTitle, popupDescription, popupButtonLabel, popupButtonUrl, popupButtonColour } = formData || {}
        return JSON.stringify({ popupTitle, popupDescription, popupButtonLabel, popupButtonUrl, popupButtonColour })
    }

    // Function to get the landing page input object
    const getLandingPageInput = (data, status) => {
        return {
            themeId: data.theme,
            title: data.title,
            domain: data.domain,
            email: data.email || '',
            status: status || originalStatus, // Use provided status or keep original
            formFields: setFormFieldsFromFormData(data),
            metaKeywords: data.metaKeywords || '',
            metaDescription: data.metaDescription || '',
            customHtmlHeader: data.customHtmlHeader || '',
            hasForm: data.hasForm === undefined ? true : data.hasForm,
            formButtonSettings: setButtonSettingsFromFormData(data),
            pageSettings: setPageSettingsFromFormData(data),
            pagePopupSettings: setPagePopupSettingsFromFormData(data),
            formContent: data.formContent || '',
            logo: data.logo || '',
            logoImage: data.logoImage !== '' ? data.logoImage : null,
            background: data.background ? data.background : 'image',
            backgroundImage: data.backgroundImage !== '' ? data.backgroundImage : null,
            backgroundVideo: data.backgroundVideo !== '' ? data.backgroundVideo : null,
            termsAndConditions: data.termsAndConditions || '',
        }
    }

    // Mutation for final saving (with redirect)
    const [finalUpdateLandingPage, mutation] = useMutation(UPDATE_LANDING_PAGE, {
        onError({ graphQLErrors }) {
            setError(graphQLErrors)
        },
        onCompleted: () => {
            navigate(`/campaigns/${data.landingPages[0]?.campaign.id}/landing-pages/`, {
                state: {
                    notification: {
                        status: 'success',
                        message: 'Landing Page has been updated successfully!',
                    },
                },
            })
        },
        variables: {
            landingPageId: landingPageId,
            landingPageInput: getLandingPageInput(updatedData),
        },
    })

    // Mutation for autosave (without redirect)
    const [autosaveUpdate] = useMutation(UPDATE_LANDING_PAGE, {
        onError({ graphQLErrors }) {
            setAutosaveStatus('error')
            console.error('Autosave error:', graphQLErrors)
            // Show toast notification for duplicate domain error
            if (
                graphQLErrors.some(
                    (error) =>
                        error.message.includes('domain is already used') ||
                        error.extensions?.code === 'DOMAIN_ALREADY_EXISTS',
                )
            ) {
                showNotification(
                    <div>
                        <p>Cannot autosave: The domain you entered is already in use.</p>
                        <p>Please enter a different domain to continue.</p>
                    </div>,
                    'error',
                    true,
                )
            }
        },
        onCompleted: () => {
            setLastSaveTime(new Date())
            setAutosaveStatus('saved')
        },
    })

    // Autosave function
    const performAutosave = useCallback(() => {
        if (!isAutosaveEnabled || _.isEmpty(updatedData)) {
            return
        }

        setAutosaveStatus('saving')

        const saveChanges = async () => {
            try {
                // Important: We maintain the original status here rather than forcing it to DRAFT
                await autosaveUpdate({
                    variables: {
                        landingPageId: landingPageId,
                        landingPageInput: getLandingPageInput(updatedData),
                    },
                })
            } catch (error) {
                console.error('Autosave failed:', error)
                setAutosaveStatus('error')
            }
        }

        saveChanges()
    }, [updatedData, landingPageId, autosaveUpdate, isAutosaveEnabled])

    // Schedule autosave when data changes
    useEffect(() => {
        if (!isAutosaveEnabled || _.isEmpty(updatedData)) return

        // Clear any existing timeout
        if (autosaveTimeoutRef.current) {
            clearTimeout(autosaveTimeoutRef.current)
        }

        // Set new timeout for autosave (3 seconds after last update)
        autosaveTimeoutRef.current = setTimeout(() => {
            performAutosave()
        }, 3000)

        // Cleanup
        return () => {
            if (autosaveTimeoutRef.current) {
                clearTimeout(autosaveTimeoutRef.current)
            }
        }
    }, [updatedData, isAutosaveEnabled, performAutosave])

    // Store the time of the last save
    const [lastSaveTime, setLastSaveTime] = useState(null)

    // Format the timestamp
    const formatTime = (date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }

    // Autosave status message component
    const AutosaveStatus = () => {
        if (!isAutosaveEnabled) return null

        let message = ''
        let bgColor = ''

        switch (autosaveStatus) {
            case 'saving':
                message = 'Saving changes...'
                bgColor = 'bg-warning'
                break
            case 'saved':
                message = lastSaveTime ? `Changes saved at ${formatTime(lastSaveTime)}` : 'Changes saved'
                bgColor = 'bg-success'
                break
            case 'error':
                message = 'Error saving changes'
                bgColor = 'bg-danger'
                break
            default:
                return null
        }

        return (
            <div className={`autosave-status ${bgColor} text-white rounded px-2 py-1 my-2`}>
                <small>{message}</small>
            </div>
        )
    }

    return (
        <Fragment>
            <ToastContainer />
            {loading || themesQuery.loading ? (
                <CubeLoader size="default" />
            ) : (
                <Card>
                    <Card.Header className="d-flex justify-content-between">
                        <div className="header-title">
                            <h4 className="card-title">
                                <i className="icon">
                                    <svg
                                        className="icon-25"
                                        width="25"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            opacity="0.4"
                                            d="M19.9926 18.9532H14.2983C13.7427 18.9532 13.2909 19.4123 13.2909 19.9766C13.2909 20.5421 13.7427 21 14.2983 21H19.9926C20.5481 21 21 20.5421 21 19.9766C21 19.4123 20.5481 18.9532 19.9926 18.9532Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            d="M10.309 6.90388L15.7049 11.264C15.835 11.3682 15.8573 11.5596 15.7557 11.6929L9.35874 20.0282C8.95662 20.5431 8.36402 20.8345 7.72908 20.8452L4.23696 20.8882C4.05071 20.8904 3.88775 20.7614 3.84542 20.5765L3.05175 17.1258C2.91419 16.4916 3.05175 15.8358 3.45388 15.3306L9.88256 6.95548C9.98627 6.82111 10.1778 6.79746 10.309 6.90388Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            opacity="0.4"
                                            d="M18.1206 8.66544L17.0804 9.96401C16.9756 10.0962 16.7872 10.1177 16.6571 10.0124C15.3925 8.98901 12.1544 6.36285 11.2559 5.63509C11.1247 5.52759 11.1067 5.33625 11.2125 5.20295L12.2157 3.95706C13.1258 2.78534 14.7131 2.67784 15.9936 3.69906L17.4645 4.87078C18.0677 5.34377 18.4698 5.96726 18.6074 6.62299C18.7661 7.3443 18.5968 8.0527 18.1206 8.66544Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                </i>{' '}
                                Creative - {data.landingPages[0].title}
                                {isDraft && <span className="ms-2 badge bg-warning">Draft</span>}
                            </h4>
                        </div>
                        {isAutosaveEnabled && <AutosaveStatus />}
                    </Card.Header>
                    <Card.Body>
                        <div className="new-user-info">
                            {isDraft && (
                                <div className="alert alert-info mb-3">
                                    <p className="mb-0">
                                        You are editing a draft. Click the Publish button to make this landing page
                                        live.
                                    </p>
                                </div>
                            )}
                            <LandingPageForm
                                data={data.landingPages[0]}
                                updateData={updateData}
                                callback={updateLandingPageCallback}
                                errors={errors}
                                mutation={mutation}
                                themes={themesQuery}
                                campaignId={data.landingPages[0].campaign.id}
                                submitButtonText={isDraft ? 'Publish' : 'Update'}
                            />
                        </div>
                    </Card.Body>
                </Card>
            )}
        </Fragment>
    )
})

export default EditLandingPage
